import { z } from 'zod';
import { ApplicantType, requiredFieldError } from '..';
import {
  E2515_CASH_APPLICATION_ANNUAL_INCOME_THRESHOLD,
  FINANCIAL_PRODUCT,
  GOVERNMENT_FINANCIAL_PRODUCTS,
} from '../constants';
import { baseResidentialApplicant, householdIncomeWithDocuments, householdSchema } from '../shared';

export const householdIncomeCashSchema = z.object({
  payment: z.object({
    isCashApplication: z.boolean({ required_error: 'Please select a payment method' }),
  }),
  applicant: z.object({
    type: z.nativeEnum(ApplicantType),
    income: householdIncomeWithDocuments.superRefine((val, ctx) => {
      // For E2515 cash applications, validate primary income verification requirements
      const householdAmount = val.householdIncome?.amount || 0;
      const isVerificationRequired = householdAmount <= E2515_CASH_APPLICATION_ANNUAL_INCOME_THRESHOLD;

      if (isVerificationRequired && !val.isSkipPrimaryIncome) {
        const primary = val.primary;
        const hasDocuments = primary?.documents && primary.documents.length > 0;
        const hasBankStatement = primary?.bankStatementComplete;

        if (!hasDocuments && !hasBankStatement) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Household income verification documents are required',
            path: ['primary', 'documents'],
          });
        }
      }
    }),
  }),
});

export const householdIncomeLoanSchema = z
  .discriminatedUnion('financialProductId', [
    z.object({
      financialProductId: z.literal(FINANCIAL_PRODUCT.FLEX),
    }),
    z.object({
      financialProductId: z.literal(FINANCIAL_PRODUCT.ELECTRIFY_2515),
      payment: z.object({
        isCashApplication: z.boolean({ required_error: 'Please select a payment method' }),
      }),
    }),
    ...[
      FINANCIAL_PRODUCT.BGL,
      FINANCIAL_PRODUCT.BPL,
      FINANCIAL_PRODUCT.HEUF_LOAN,
      FINANCIAL_PRODUCT.BRIGHTE_PAY,
      FINANCIAL_PRODUCT.BHESL,
      ...GOVERNMENT_FINANCIAL_PRODUCTS,
    ].map(product =>
      z.object({
        financialProductId: z.literal(product),
        hasJointApplicant: z.boolean().optional(),
        jointApplicant: baseResidentialApplicant
          .pick({
            firstName: true,
            lastName: true,
            email: true,
            phone: true,
          })
          .optional(),
      }),
    ),
  ])
  .and(householdSchema)
  .superRefine((val, ctx) => {
    if ('applicant' in val && val?.applicant.maritalStatus !== 'single' && 'hasJointApplicant' in val) {
      if (val.hasJointApplicant === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: requiredFieldError(),
          path: ['hasJointApplicant'],
        });
      }

      if (val.hasJointApplicant) {
        Object.keys(val.jointApplicant || {}).forEach(key => {
          if (!val.jointApplicant?.[key as keyof typeof val.jointApplicant]) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: requiredFieldError(key),
              path: ['jointApplicant', key],
            });
          }
        });
      }
    }
    if (
      val.financialProductId === FINANCIAL_PRODUCT.ELECTRIFY_2515 &&
      'payment' in val &&
      !val.payment.isCashApplication
    ) {
      if (!val.applicant.maritalStatus) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: requiredFieldError(),
          path: ['applicant.maritalStatus'],
        });
      }
    }
  });

export type HouseholdType = z.infer<typeof householdSchema> & {
  hasJointApplicant?: boolean;
  jointApplicant?: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
  payment?: {
    isCashApplication?: boolean;
  };
};
